import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  Users,
  FileBarChart,
  LogOut,
  Settings as SettingsIcon,
  Undo2,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "../lib/auth";

const navItems = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, testid: "nav-dashboard", key: "01" },
  { to: "/pos", label: "Point of Sale", icon: ShoppingCart, testid: "nav-pos", key: "02" },
  { to: "/inventory", label: "Inventory", icon: Package, testid: "nav-inventory", key: "03" },
  { to: "/ledger", label: "Party Ledger", icon: Users, testid: "nav-ledger", key: "04" },
  { to: "/returns", label: "Returns", icon: Undo2, testid: "nav-returns", key: "05" },
  { to: "/reports", label: "Reports", icon: FileBarChart, testid: "nav-reports", key: "06", adminOnly: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", key: "07" },
];

function useNow() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

export default function Layout() {
  const { logout, email, role } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const now = useNow();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer when navigating on mobile
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const items = navItems.filter((it) => !it.adminOnly || role === "admin");

  const timeStr = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const dateStr = now.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });

  const SidebarContent = (
    <>
      {/* Brand block */}
      <div className="px-6 pt-7 pb-6 relative">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-md flex items-center justify-center shrink-0 relative overflow-hidden"
            style={{
              background:
                "radial-gradient(circle at 30% 30%, rgba(59,130,246,0.25), rgba(30,64,175,0.15) 60%, transparent 100%)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <img
              src="/logo.png"
              alt="Afridi & Co."
              className="w-11 h-11 object-contain"
              style={{ filter: "drop-shadow(0 2px 6px rgba(30,64,175,0.35))" }}
            />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-serif text-white text-[22px] leading-none">
              Afridi<span className="text-blue-400">&amp;</span>Co.
            </span>
            <span className="text-[9.5px] uppercase tracking-[0.28em] text-slate-500 mt-1">
              Heavy Machinery · POS
            </span>
          </div>
        </div>
        {/* Close button (mobile drawer only) */}
        <button
          onClick={() => setDrawerOpen(false)}
          data-testid="sidebar-close-btn"
          className="lg:hidden absolute top-6 right-4 text-slate-400 hover:text-white"
          aria-label="Close menu"
        >
          <X size={20} />
        </button>
      </div>

      {/* Live clock strip */}
      <div
        className="mx-4 mb-3 px-3 py-2.5 rounded-md flex items-center justify-between"
        style={{
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-[10px] uppercase tracking-[0.24em] text-slate-500">Live</span>
        </div>
        <div className="text-right">
          <div className="font-mono-tabular text-[13px] text-white leading-none">{timeStr}</div>
          <div className="text-[9.5px] uppercase tracking-[0.24em] text-slate-500 mt-0.5">
            {dateStr}
          </div>
        </div>
      </div>

      {/* Nav header */}
      <div className="px-6 pb-2 pt-1 flex items-center justify-between">
        <div className="text-[9.5px] uppercase tracking-[0.28em] text-slate-500">Modules</div>
        <div className="text-[9.5px] font-mono-tabular text-slate-600">
          {items.length.toString().padStart(2, "0")}
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 pr-2 overflow-y-auto">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            className={({ isActive }) =>
              `sidebar-item flex items-center gap-3 pl-6 pr-4 py-2.5 text-[13px] ${
                isActive ? "active" : ""
              }`
            }
          >
            <span className="font-mono-tabular text-[10px] text-slate-600 w-6 shrink-0">
              {item.key}
            </span>
            <item.icon size={16} strokeWidth={1.6} />
            <span className="flex-1">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Footer user block */}
      <div className="px-4 pt-4 pb-5 border-t border-white/5 mt-2">
        <div
          className="flex items-center gap-3 px-3 py-2.5 rounded-md mb-2"
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-[13px] text-white font-medium shrink-0"
            style={{
              background: role === "admin"
                ? "linear-gradient(135deg, #f59e0b, #b45309)"
                : "linear-gradient(135deg, #3b82f6, #1e40af)",
            }}
          >
            {(email || "A").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[9.5px] uppercase tracking-[0.24em] text-slate-500">
              {role === "admin" ? "Admin" : role === "shop" ? "Shop Operator" : "Operator"}
            </div>
            <div className="text-[12px] text-slate-200 truncate" data-testid="user-email">
              {email || "—"}
            </div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.2em] rounded-md text-slate-400 border border-white/10 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500/30 transition-colors duration-150"
        >
          <LogOut size={13} strokeWidth={1.8} />
          <span>Sign out</span>
        </button>
      </div>
    </>
  );

  const sidebarStyle = {
    background: "#05070d",
    color: "#e5e7eb",
    borderRight: "1px solid rgba(255,255,255,0.05)",
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--paper)" }}>
      {/* Desktop sidebar (fixed) */}
      <aside
        className="hidden lg:flex w-[260px] fixed inset-y-0 left-0 flex-col no-print"
        style={sidebarStyle}
        data-testid="sidebar"
      >
        {SidebarContent}
      </aside>

      {/* Mobile top bar */}
      <div
        className="lg:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 h-14 no-print"
        style={{
          background: "#05070d",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <button
          onClick={() => setDrawerOpen(true)}
          data-testid="sidebar-open-btn"
          className="text-slate-300 hover:text-white p-1 -ml-1"
          aria-label="Open menu"
        >
          <Menu size={22} />
        </button>
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="Afridi & Co." className="w-7 h-7 object-contain" />
          <span className="font-serif text-white text-[16px] leading-none">
            Afridi<span className="text-blue-400">&amp;</span>Co.
          </span>
        </div>
        <div className="w-6" />
      </div>

      {/* Mobile drawer + backdrop */}
      {drawerOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black/60 no-print"
          onClick={() => setDrawerOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-[260px] max-w-[85vw] flex flex-col no-print transition-transform duration-300 ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={sidebarStyle}
        data-testid="sidebar-mobile"
      >
        {SidebarContent}
      </aside>

      {/* Main */}
      <main className="flex-1 lg:ml-[260px] min-h-screen pt-14 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
